import { useEffect, useRef, useCallback } from 'react';
import type { ReactNode } from 'react';
import { X } from 'lucide-react';

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  icon?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  busy?: boolean;
  maxWidth?: string;
  // Off for a long, costly-to-redo form (e.g. NewOrderModal, mid-way
  // through scanning a stack of serials) — the same reasoning as the
  // deliberate no-click-outside-to-close below, just for the other way a
  // long form gets accidentally wiped: a stray Escape keypress.
  closeOnEscape?: boolean;
}

export function Modal({
  open,
  onClose,
  title,
  subtitle,
  icon,
  children,
  footer,
  busy = false,
  maxWidth = 'max-w-lg',
  closeOnEscape = true,
}: ModalProps) {
  const dialogRef = useRef<HTMLDivElement>(null);

  const close = useCallback(() => {
    if (!busy) onClose();
  }, [busy, onClose]);

  // Focus the dialog once per open — must not depend on `close`, which is a
  // new function identity on every parent re-render (e.g. every keystroke in
  // a form field), or it re-steals focus from whatever input the user is
  // typing into.
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => dialogRef.current?.focus(), 30);
    return () => clearTimeout(t);
  }, [open]);

  useEffect(() => {
    if (!open || !closeOnEscape) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, close, closeOnEscape]);

  if (!open) return null;

  return (
    <div
      // Deliberately no click-outside-to-close — an accidental click just
      // past the dialog's edge (easy to do on a long form) used to lose
      // whatever was being filled in. Only the X button (or Escape) closes it.
      className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-primary-900/40 backdrop-blur-[3px] animate-[fadeIn_0.15s_ease-out]"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        ref={dialogRef}
        className={`w-full ${maxWidth} rounded-2xl overflow-hidden flex flex-col outline-none bg-white shadow-modal animate-[popIn_0.18s_ease-premium]`}
        style={{ maxHeight: '92vh' }}
      >
        <div className="flex items-center justify-between px-5 py-3.5 flex-shrink-0 bg-gradient-to-br from-primary-800 via-primary-700 to-primary-500">
          <div className="flex items-center gap-2 min-w-0">
            {icon && (
              <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-white/15 text-white flex-shrink-0">
                {icon}
              </div>
            )}
            <div className="min-w-0">
              <p className="text-[13px] font-bold text-white leading-tight truncate">{title}</p>
              {subtitle && <p className="text-[10px] leading-tight truncate text-white/60">{subtitle}</p>}
            </div>
          </div>
          <button
            onClick={close}
            className="w-7 h-7 flex items-center justify-center rounded-lg cursor-pointer flex-shrink-0 bg-white/10 text-white/85 hover:bg-white/20"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-5 space-y-3.5">{children}</div>
        {footer && (
          <div className="flex gap-2 px-5 py-3.5 flex-shrink-0 border-t border-border-subtle bg-surface-subtle">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}
